import { MutationTree } from 'vuex'
import { omit } from 'lodash'
import { MutationTypes } from './constants'
import { State } from './state'
import { Slide, PPTElement, SlideTheme } from '@/types/slides'
import { CreatingElement } from '@/types/edit'
import { ToolbarState } from '@/types/toolbar'
import { TextAttrs } from '@/utils/prosemirror/utils'

interface RemoveElementPropData {
  id: string;
  propName: string | string[];
}

interface UpdateElementData {
  id: string | string[];
  props: Partial<PPTElement>;
}

export const mutations: MutationTree<State> = {

  // editor

  [MutationTypes.SET_ACTIVE_ELEMENT_ID_LIST](state, activeElementIdList: string[]) {
    if (activeElementIdList.length === 1) state.handleElementId = activeElementIdList[0]
    else state.handleElementId = ''
    state.activeElementIdList = activeElementIdList
  },
  [MutationTypes.SET_HIDDEN_ELEMENT_ID_LIST](state, hiddenElementIdList: string[]) {
    state.hiddenElementIdList = hiddenElementIdList
  },
  [MutationTypes.SET_SELECT_PANEL_STATE](state, show: boolean) {
    state.showSelectPanel = show
  },
  [MutationTypes.SET_HANDLE_ELEMENT_ID](state, handleElementId: string) {
    state.handleElementId = handleElementId
  },
  
  [MutationTypes.SET_ACTIVE_GROUP_ELEMENT_ID](state, activeGroupElementId: string) {
    state.activeGroupElementId = activeGroupElementId
  },

  [MutationTypes.SET_CANVAS_PERCENTAGE](state, percentage: number) {
    state.canvasPercentage = percentage
  },

  [MutationTypes.SET_CANVAS_WIDTH](state, value: number) {
    state.canvasWidth = value
  },
  [MutationTypes.SET_CANVAS_HEIGHT](state, value: number) {
    state.canvasHeight = value
  },
  [MutationTypes.SET_CANVAS_SCALE](state, scale: number) {

    state.canvasScale = scale
  },

  [MutationTypes.SET_THUMBNAILS_FOCUS](state, isFocus: boolean) {
    state.thumbnailsFocus = isFocus
  },

  [MutationTypes.SET_EDITORAREA_FOCUS](state, isFocus: boolean) {
    state.editorAreaFocus = isFocus
  },

  [MutationTypes.SET_DISABLE_HOTKEYS_STATE](state, disable: boolean) {
    state.disableHotkeys = disable
  },

  [MutationTypes.SET_GRID_LINES_STATE](state, show: boolean) {
    state.showGridLines = show
  },

  [MutationTypes.SET_CREATING_ELEMENT](state, element: CreatingElement | null) {
    state.creatingElement = element
  },
  [MutationTypes.SET_COPY_STATE](state, isCopy) {
    state.isElementCopy = isCopy
  },
  [MutationTypes.SET_AVAILABLE_FONTS](state) {
    const defaults = [{ label: '思源黑体 常规', value: 'Noto Sans S Chinese Regular'}]
    state.availableFonts = defaults as never
  },

  [MutationTypes.SET_TOOLBAR_STATE](state, toolbarState: ToolbarState) {
    state.toolbarState = toolbarState
  },

  [MutationTypes.SET_CLIPING_IMAGE_ELEMENT_ID](state, elId: string) {
    state.clipingImageElementId = elId
  },

  [MutationTypes.SET_RICHTEXT_ATTRS](state, attrs: TextAttrs) {
    state.richTextAttrs = attrs
  },

  [MutationTypes.SET_SELECTED_TABLE_CELLS](state, cells: string[]) {
    state.selectedTableCells = cells
  },

  [MutationTypes.SET_SCALING_STATE](state, isScaling: boolean) {
    state.isScaling = isScaling
  },
  [MutationTypes.SET_SCALING_COMMAND](state, command: string) {
    state.scalingCommand = command
  },
  [MutationTypes.SET_EDITING_SHAPE_ELEMENT_ID](state, ellId: string) {
    state.editingShapeElementId = ellId
  },
  [MutationTypes.SET_EDITING_RECT_ELEMENT_ID](state, ellId: string) {
    state.editingRectElementId = ellId
  },
  [MutationTypes.SET_EDITING_TEXT_ELEMENT_ID](state, ellId: string) {
    state.editingTextElementId = ellId
  },
  [MutationTypes.SET_EDITING_CLIP](state, isScaling: boolean) {
    state.editingClip = isScaling
  },
  // slides
  
  [MutationTypes.SAVE_SPINNING](state, spinning: boolean) {
    state.saveSpinning = spinning
  },
  [MutationTypes.SET_TITLE](state, title: string) {
    state.slideTitle = title
  },
  [MutationTypes.SET_THEME](state, themeProps: Partial<SlideTheme>) {
    state.theme = { ...state.theme, ...themeProps }
  },

  [MutationTypes.SET_VIEWPORT_RATIO](state, viewportRatio: number) {
    state.viewportRatio = viewportRatio
  },
  [MutationTypes.SET_VIEWPORT_RATIO_WIDTH](state, width: number) { 
    state.viewportWidth = width
  },
  [MutationTypes.SET_VIEWPORT_RATIO_WIDTHS](state, width: number) {
    state.viewportWidths = width
  },
  [MutationTypes.SET_VIEWPORT_RATIO_HEIGHTS](state, height: number) {
    state.viewportHeights = height
  },
  [MutationTypes.SET_VIEWPORT_RATIO_HEIGHT](state, height: number) {
    state.viewportHeight = height
  },
  [MutationTypes.SET_VIEWPORT_RATIO_TYPE](state, type: string) {
    state.viewportType = type
  },
  [MutationTypes.SET_SLIDES](state, slides: Slide[]) {
    state.slides = slides
    
  },
  [MutationTypes.SET_SLIDES_DATA](state, data) {
    state.slidesData = data
  },
  [MutationTypes.SET_TEMPLATE_DATA](state, data) {
    state.templateData = data
  },
  [MutationTypes.SET_VIP_SHOW](state, show) {
    state.isVipVisible = show
  },
  [MutationTypes.SET_COPY_SHOW](state, show) {
    state.isCopyVisible = show
  },
  
  [MutationTypes.ADD_SLIDE](state, slide: Slide | Slide[]) {
    const slides = Array.isArray(slide) ? slide : [slide]
    const addIndex = state.slideIndex + 1
    state.slides.splice(addIndex, 0, ...slides)
    state.slideIndex = addIndex
  },

  [MutationTypes.UPDATE_SLIDE](state, props: Partial<Slide>) {
    const slideIndex = state.slideIndex
    state.slides[slideIndex] = { ...state.slides[slideIndex], ...props }
  },

  [MutationTypes.DELETE_SLIDE](state, slideId: string | string[]) {
    const slidesId = Array.isArray(slideId) ? slideId : [slideId]

    const deleteSlidesIndex = []
    for (let i = 0; i < slidesId.length; i++) {
      const index = state.slides.findIndex(item => item.id === slidesId[i])
      deleteSlidesIndex.push(index)
    }
    let newIndex = Math.min(...deleteSlidesIndex)

    const maxIndex = state.slides.length - slidesId.length - 1
    if (newIndex > maxIndex) newIndex = maxIndex

    state.slideIndex = newIndex
    state.slides = state.slides.filter(item => !slidesId.includes(item.id))
  },

  [MutationTypes.UPDATE_SLIDE_INDEX](state, index: number) {
    state.slideIndex = index
  },

  [MutationTypes.UPDATE_SELECTED_SLIDES_INDEX](state, selectedSlidesIndex: number[]) {
    state.selectedSlidesIndex = selectedSlidesIndex
  },

  [MutationTypes.ADD_ELEMENT](state, element: PPTElement | PPTElement[]) {
    const elements = Array.isArray(element) ? element : [element]
    const currentSlideEls = state.slides[state.slideIndex].elements
    const newEls = [...currentSlideEls, ...elements]
    state.slides[state.slideIndex].elements = newEls
    
  },
  [MutationTypes.ADD_GROUP_ELEMENT](state, element) {
    const currentSlideEls = state.slides[state.slideIndex].elements
    const newEls = [...currentSlideEls, ...element]
    state.slides[state.slideIndex].elements = newEls
  },
  [MutationTypes.UPDATE_ELEMENT](state,data: UpdateElementData) {
    const { id, props } = data
    const elIdList = typeof id === 'string' ? [id] : id
    const slideIndex = state.slideIndex
    const slide = state.slides[slideIndex]
    const elements = slide.elements.map(el => {
      return elIdList.includes(el.id) ? { ...el, ...props } : el
    })
    state.slides[slideIndex].elements = (elements as PPTElement[])
  },

  [MutationTypes.REMOVE_ELEMENT_PROPS](state, data: RemoveElementPropData) {
    const { id, propName } = data
    const propsNames = typeof propName === 'string' ? [propName] : propName

    const slideIndex = state.slideIndex
    const slide = state.slides[slideIndex]
    
    const elements = slide.elements.map(el => {
      return el.id === id ? omit(el, propsNames) : el
    })
    state.slides[slideIndex].elements = (elements as PPTElement[])
   
  },
  [MutationTypes.SET_SCENCE](state, data) {
    state.currScence = data
  },
  [MutationTypes.SET_PREVIEW_URL](state, data) {
    state.currPreviewUrl = data
  },
  [MutationTypes.SET_SLIDES_FONTS](state, data) {
    state.slideFonts = data
  },
  [MutationTypes.SET_SLIDES_FONTS_LIST](state, data) {
    state.slidesFontsList = data
  },
  [MutationTypes.SET_SLIDES_USE_FONTS_LIST](state, data) {
    state.slidesUseFontsList = data
  },
  // snapshot

  [MutationTypes.SET_SNAPSHOT_CURSOR](state, cursor: number) {
    state.snapshotCursor = cursor
  },

  [MutationTypes.SET_SNAPSHOT_LENGTH](state, length: number) {
    state.snapshotLength = length
  },

  // keyboard

  [MutationTypes.SET_CTRL_KEY_STATE](state, isActive: boolean) {
    state.ctrlKeyState = isActive
  },
  [MutationTypes.SET_SHIFT_KEY_STATE](state, isActive: boolean) {
    state.shiftKeyState = isActive
  },
  [MutationTypes.SET_SPACE_KEY_STATE](state, isActive: boolean) {
    state.spaceKeyState = isActive
  },

  [MutationTypes.SET_CANVAS_DRAGGED](state, isDragged: boolean) {
    state.canvasDragged = isDragged
  },
  // screen

  [MutationTypes.SET_SCREENING](state, screening) {
    state.screening = screening
  },

  // layouts
  [MutationTypes.SET_LAYOUTS](state, layouts) {
    state.layouts = layouts
  },
  [MutationTypes.SET_TEMP_STATE](state, isShow: boolean) {
    state.tempState = isShow
  },
  [MutationTypes.SET_LAY_STATE](state, isShow: boolean) {
    state.layState = isShow
  },
  [MutationTypes.SET_CRATED_DESIGN_MODAL_STATE](state, isShow: boolean) {
    state.createDesignModalState = isShow
  },
  // handler
  [MutationTypes.SET_ELE_DRAWER_STATE](state, isShow: boolean) {
    state.eleDrawerState = isShow
  },
  [MutationTypes.SET_ELE_DRAWER_TYPE](state, type: string) {
    state.eleDrawerType = type
  },
  [MutationTypes.SET_ELE_THUMB_STATE](state, isShow: boolean) {
    state.eleThumbState = isShow
  },
  

  [MutationTypes.SET_TEMP_MODAL_STATE](state, isShow: boolean) {
    state.tempModalState = isShow
  },
  [MutationTypes.SET_IMAGES_MODAL_STATE](state, isShow: boolean) {
    state.imagesModalState = isShow
  },
  [MutationTypes.SET_TAG_ALL_ELE_STATE](state, isShow: boolean) {
    state.tagAllEleState = isShow
  },

  // login 
  [MutationTypes.SET_USER_INFO](state, data) {
    state.userInfo = data
  },
  [MutationTypes.SET_LOGIN_MODAL_STATE](state, isShow: boolean) {
    state.loginModalState = isShow
  },
  [MutationTypes.SET_LOGIN_MODAL_TYPE](state, type: string) {
    state.loginModalType = type
  },

  // search
  [MutationTypes.SET_SEARCH_KEY](state, key: string) {
    state.seachKey = key
  },
  [MutationTypes.SET_SEARCH_TOTAL](state, key: number) {
    state.seachTotal = key
  },
  [MutationTypes.SET_SEARCH_HISTORY](state) {
    const search_history = localStorage.getItem('search_history')
    let history = []
    if (search_history) {
      history = JSON.parse(search_history)
    }
    else {
      history = []
    }
    state.searchHistoryList = history
  },

  // publish
  [MutationTypes.PUBLISH_TEMPLATE_VISIBLE](state, key: boolean) {
    state.publishTemplateVisible = key
  },
  [MutationTypes.PUBLISH_MATERIAL_VISIBLE](state, key: boolean) {
    state.publishMaterialVisible = key
  },
  
  // 
  [MutationTypes.ALLOW_BACK](state, key: boolean) {
    state.currentAllowBack = key
  },
  
  // download
  [MutationTypes.SET_DWONLOAD_INDEX](state, index: number) {
    state.downSlideIndex = index
  },
  [MutationTypes.SET_EXPORTING_STATE](state, key:boolean) {
    state.exporting = key
  },
  // effect
  [MutationTypes.SET_EFFECT_NUM](state, num: number) {
    state.effectNum = num
  },

  [MutationTypes.SET_SAVE_TIME](state, time:string) {
    state.saveTime = time
  },

  [MutationTypes.SET_VERTICAL_LINE](state, show:boolean) {
    state.showVerticalLine = show
  },

  [MutationTypes.SET_HORIZONTAL_LINE](state, show:boolean) {
    state.showHorizontalLine = show
  },
  [MutationTypes.SET_HOTKEY](state, show:boolean) {
    state.showHotkey = show
  },
  
  [MutationTypes.SET_ISFRIST_SAVE](state, show:boolean) {
    state.isFirstSave = show
  },
  [MutationTypes.CREATE_CODEID_VALUE](state, arr:any) {
    state.createID = arr
  },
  [MutationTypes.SET_OPENMORE_VALUE](state, show:boolean) {
    state.openMoreIshow = show
  },
  [MutationTypes.SET_VERSION_SHOW](state, show:boolean) {
    state.isVersionShow = show
  },
  // 协作
  [MutationTypes.SET_INVITE_INFO](state, info:any) {
    state.inviteInfo = info
  },
  // 协作
  [MutationTypes.SET_EIDT_LIST](state, info:any) {
    state.editList = info
  },
  [MutationTypes.SET_TOKEN](state, info:any) {
    state.token = info
  },
  [MutationTypes.SET_SVG_SHOW](state, show:boolean) {
    state.svgDialogVisible = show
  },
  [MutationTypes.SET_SVG_TYPE](state, info:any) {
    state.svgUploadType = info
  },
  
  // 移动端
  [MutationTypes.SET_M_PAGE_SHOW](state, show:boolean) {
    state.mPage = show
  },
  [MutationTypes.SET_M_TEMPLATE_SHOW](state, show:boolean) {
    state.mTemplate = show
  },
  [MutationTypes.SET_M_USER_SHOW](state, show:boolean) {
    state.mUser = show
  },
  [MutationTypes.SET_M_ADD_SHOW](state, show:boolean) {
    state.mAdd = show
  },
  [MutationTypes.SET_M_IMAGES_SHOW](state, show:boolean) {
    state.mImages = show
  },
  [MutationTypes.SET_M_MATERIAL_SHOW](state, show:boolean) {
    state.mMaterial = show
  },
  [MutationTypes.SET_M_BACKGROUND_SHOW](state, show:boolean) {
    state.mBackground = show
  },  
}


